import { useMemo, useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, Plus, Loader2, Check } from "lucide-react";
import { listMasters, createMaster } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MasterCombobox — an autocomplete input backed by a master list.
 *
 * Props:
 *   masterType    "rm_grade" | "sfg_sku" | "fg_sku" | "supplier" | "customer" | "press"
 *   value         current string value (canonical name)
 *   onChange      (name) => void, called with the canonical name
 *   onPickMaster  (master|null) => void, called with the full master doc when the user picks
 *                 one from the list, or null when they free-type an unmatched string.
 *                 Use this to persist a master-id link on the parent doc.
 *   placeholder   string
 *   testId        base test id
 *   allowFreeText If true, user can type anything without proposing. Default false.
 */
const MasterCombobox = ({
  masterType, value = "", onChange, onPickMaster,
  placeholder = "", testId = "master-combobox", allowFreeText = false,
}) => {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const wrapRef = useRef(null);

  const { data: masters = [] } = useQuery({
    queryKey: ["masters", masterType, "active"],
    queryFn: () => listMasters({ master_type: masterType, status: "active" }),
    staleTime: 30_000,
  });

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onClickOut = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOut);
    return () => document.removeEventListener("mousedown", onClickOut);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (masters || []).filter((m) => m.active && m.name.toLowerCase().includes(q)).slice(0, 20),
    [masters, q],
  );

  const exact = masters.find((m) => m.active && m.name.toLowerCase() === q);
  const canPropose = !allowFreeText && q && !exact;

  const proposeMut = useMutation({
    mutationFn: (name) => createMaster({ master_type: masterType, name }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["masters"] });
      if (d.status === "pending") {
        toast.success(`"${d.name}" proposed — pending director approval. It'll appear here once approved.`);
      } else {
        toast.success(`Added ${d.name}`);
        onChange?.(d.name);
        onPickMaster?.(d);
      }
      setOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to propose"),
  });

  const pick = (m) => {
    setQuery(m.name);
    onChange?.(m.name);
    onPickMaster?.(m);
    setOpen(false);
  };

  const handleInputChange = (v) => {
    setQuery(v);
    setOpen(true);
    if (allowFreeText) {
      onChange?.(v);
      // Free-typed text breaks the master link — parent should drop customer_id.
      const match = masters.find((m) => m.active && m.name.toLowerCase() === v.trim().toLowerCase());
      onPickMaster?.(match || null);
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full" data-testid={testId}>
      <div className="relative">
        <input
          data-testid={`${testId}-input`}
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full h-10 pl-2 pr-8 border border-slate-300 rounded-sm text-sm font-mono uppercase"
          autoComplete="off"
        />
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      </div>

      {open && (
        <div data-testid={`${testId}-dropdown`} className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-60 overflow-auto">
          {filtered.length === 0 && !canPropose && (
            <p className="text-xs text-slate-400 px-3 py-2">No matches.</p>
          )}
          {filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`${testId}-opt-${m.name}`}
              onClick={() => pick(m)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 flex items-center justify-between"
            >
              <span className="font-mono text-slate-900">{m.name}</span>
              {value === m.name && <Check className="h-3.5 w-3.5 text-green-600" />}
            </button>
          ))}
          {canPropose && (
            <button
              type="button"
              data-testid={`${testId}-propose`}
              onClick={() => proposeMut.mutate(query.trim())}
              disabled={proposeMut.isPending}
              className="w-full text-left px-3 py-2 text-sm border-t border-slate-100 bg-slate-50 hover:bg-slate-100 flex items-center gap-2 text-slate-700 font-semibold"
            >
              {proposeMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {isDirector ? `Add "${query.trim()}" as new ${masterType}` : `Propose "${query.trim()}" as new ${masterType}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MasterCombobox;
